import React from 'react';
import { Link, router } from '@inertiajs/react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Plus, Edit, Trash2, ExternalLink, GripVertical } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

type FeeWaiverPartner = {
  id: number;
  name: string;
  details: string | null;
  requirements: string | null;
  code: string | null;
  link: string | null;
  position: number;
  published: boolean;
};

type Props = {
  fee_waiver_partners: FeeWaiverPartner[];
};

export default function FeeWaiverPartnersIndex({ fee_waiver_partners }: Props) {
  const handleDelete = (id: number) => {
    router.delete(`/admin/fee_waiver_partners/${id}`);
  };

  return (
    <AdminLayout title="Fee Waiver Partners">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Fee Waiver Partners</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage university partnerships for application fee waivers
          </p>
        </div>
        <Link href="/admin/fee_waiver_partners/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Partner
          </Button>
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {fee_waiver_partners.length === 0 ? (
            <li className="px-6 py-12 text-center text-gray-500">
              No fee waiver partners yet. Add your first partner to get started.
            </li>
          ) : (
            fee_waiver_partners.map((partner) => (
              <li key={partner.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      <GripVertical className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {partner.name}
                        </h3>
                        <Badge variant={partner.published ? 'default' : 'secondary'}>
                          {partner.published ? 'Published' : 'Draft'}
                        </Badge>
                      </div>
                      {partner.details && (
                        <p className="text-sm text-gray-600 mb-1">{partner.details}</p>
                      )}
                      <div className="space-y-1">
                        {partner.code && (
                          <p className="text-sm">
                            <span className="font-medium text-gray-700">Code:</span>{' '}
                            <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">
                              {partner.code}
                            </span>
                          </p>
                        )}
                        {partner.requirements && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {partner.requirements}
                          </p>
                        )}
                        {partner.link && (
                          <a
                            href={partner.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-primary hover:text-primary/80"
                          >
                            View partner website
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Link href={`/admin/fee_waiver_partners/${partner.id}/edit`}>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Fee Waiver Partner</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{partner.name}"? This action cannot be
                            undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(partner.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </AdminLayout>
  );
}